/**
 * Provider-agnostic product / marketplace readiness.
 *
 * Adapters contribute field requirements. This module never calls marketplace
 * APIs — it only projects whether canonical Tradivo data would satisfy a
 * connected channel later.
 *
 * Amazon AU readiness is fail-closed via `evaluateAmazonAuReadiness`. Hardcoded
 * brand/GTIN/images must never produce Amazon READY.
 */

import {
  evaluateAmazonAuReadiness,
  isAmazonProvider,
  type AmazonPtdEvaluation,
  type AmazonProductTypeSelection,
  type AmazonSchemaIdentity,
} from "@/lib/providers/amazon-au";
import type { AmazonListingShippingConfig } from "@/lib/providers/amazon-merchant-shipping";
import type { ShippingDimensions } from "@/lib/shipping-facts";

export type MarketplaceReadinessStatus =
  | "READY"
  | "MISSING_REQUIRED_DATA"
  | "NEEDS_MAPPING"
  | "NEEDS_APPROVAL"
  | "CHANNEL_NOT_CONNECTED"
  | "PROVIDER_ERROR"
  | "NOT_EVALUATED";

export type CanonicalFieldKey =
  | "name"
  | "sku"
  | "brand"
  | "manufacturer"
  | "mpn"
  | "condition"
  | "category"
  | "gtin"
  | "upc"
  | "ean"
  | "barcode"
  | "weight"
  | "dimensions"
  | "description"
  | "shortDescription"
  | "images"
  | "price"
  | "supplier"
  | "countryOfOrigin"
  | "material";

export interface CanonicalProductSnapshot {
  name: string;
  sku?: string | null;
  brand?: string | null;
  manufacturer?: string | null;
  mpn?: string | null;
  condition?: string | null;
  categoryId?: string | null;
  gtin?: string | null;
  upc?: string | null;
  ean?: string | null;
  barcode?: string | null;
  identifiersNotApplicable?: boolean;
  weightKg?: number | null;
  lengthCm?: number | null;
  widthCm?: number | null;
  heightCm?: number | null;
  description?: string | null;
  shortDescription?: string | null;
  imageCount?: number;
  sellingPrice?: number | null;
  hasSupplier?: boolean;
  countryOfOrigin?: string | null;
  material?: string | null;
}

export interface CanonicalGap {
  field: CanonicalFieldKey;
  label: string;
  /** Recommended for catalogue quality — never a Tradivo create blocker. */
  recommended: boolean;
}

export interface ProviderFieldRequirement {
  field: CanonicalFieldKey;
  label: string;
}

export interface MarketplaceReadinessAdapter {
  provider: string;
  displayName: string;
  requiredFields: ProviderFieldRequirement[];
}

export interface ProviderReadiness {
  provider: string;
  displayName: string;
  status: MarketplaceReadinessStatus;
  missingFields: { field: CanonicalFieldKey; label: string }[];
  summary: string;
}

const CANONICAL_RECOMMENDED: { field: CanonicalFieldKey; label: string; present: (p: CanonicalProductSnapshot) => boolean }[] = [
  { field: "brand", label: "Brand", present: (p) => Boolean(p.brand?.trim()) },
  { field: "gtin", label: "GTIN / UPC / EAN", present: (p) => Boolean(p.gtin?.trim() || p.upc?.trim() || p.ean?.trim() || p.identifiersNotApplicable) },
  { field: "condition", label: "Condition", present: (p) => Boolean(p.condition?.trim()) },
  { field: "weight", label: "Package / shipping weight", present: (p) => p.weightKg != null && Number(p.weightKg) > 0 },
  { field: "dimensions", label: "Package / shipping dimensions", present: (p) => p.lengthCm != null && p.widthCm != null && p.heightCm != null },
  { field: "description", label: "Description", present: (p) => Boolean(p.description?.trim()) },
  { field: "images", label: "Images", present: (p) => (p.imageCount ?? 0) > 0 },
  { field: "countryOfOrigin", label: "Country of origin", present: (p) => Boolean(p.countryOfOrigin?.trim()) },
  { field: "material", label: "Material", present: (p) => Boolean(p.material?.trim()) },
];

export function evaluateCanonicalCompleteness(product: CanonicalProductSnapshot): {
  complete: boolean;
  gaps: CanonicalGap[];
  summary: string;
} {
  const gaps: CanonicalGap[] = CANONICAL_RECOMMENDED.filter((row) => !row.present(product)).map((row) => ({
    field: row.field,
    label: row.label,
    recommended: true,
  }));
  const complete = gaps.length === 0;
  return {
    complete,
    gaps,
    summary: complete ? "Product details: Well prepared" : `Product details: Some details missing — ${gaps.map((g) => g.label).join(" · ")}`,
  };
}

/** Built-in requirement catalogues. No live Amazon/Temu/eBay API coupling. */
export const builtinMarketplaceAdapters: MarketplaceReadinessAdapter[] = [
  {
    provider: "ebay",
    displayName: "eBay",
    requiredFields: [
      { field: "name", label: "Title" },
      { field: "description", label: "Description" },
      { field: "images", label: "Images" },
      { field: "price", label: "Price" },
      { field: "condition", label: "Condition" },
    ],
  },
  {
    provider: "bigcommerce",
    displayName: "BigCommerce",
    requiredFields: [
      { field: "name", label: "Name" },
      { field: "price", label: "Price" },
    ],
  },
  {
    provider: "amazon",
    displayName: "Amazon",
    // Not readiness authority. Amazon required fields come from PTD (020D+).
    requiredFields: [],
  },
  {
    provider: "temu",
    displayName: "Temu",
    requiredFields: [
      { field: "name", label: "Title" },
      { field: "images", label: "Images" },
      { field: "price", label: "Price" },
      { field: "weight", label: "Weight" },
    ],
  },
];

export function fieldPresent(product: CanonicalProductSnapshot, field: CanonicalFieldKey): boolean {
  switch (field) {
    case "name":
      return Boolean(product.name?.trim());
    case "sku":
      return Boolean(product.sku?.trim());
    case "brand":
      return Boolean(product.brand?.trim());
    case "manufacturer":
      return Boolean(product.manufacturer?.trim());
    case "mpn":
      return Boolean(product.mpn?.trim());
    case "condition":
      return Boolean(product.condition?.trim());
    case "category":
      return Boolean(product.categoryId);
    case "gtin":
      return Boolean(product.gtin?.trim() || product.upc?.trim() || product.ean?.trim() || product.identifiersNotApplicable);
    case "upc":
      return Boolean(product.upc?.trim() || product.gtin?.trim() || product.identifiersNotApplicable);
    case "ean":
      return Boolean(product.ean?.trim() || product.gtin?.trim() || product.identifiersNotApplicable);
    case "barcode":
      return Boolean(product.barcode?.trim());
    case "weight":
      return product.weightKg != null && Number(product.weightKg) > 0;
    case "dimensions":
      return product.lengthCm != null && product.widthCm != null && product.heightCm != null;
    case "description":
      return Boolean(product.description?.trim());
    case "shortDescription":
      return Boolean(product.shortDescription?.trim());
    case "images":
      return (product.imageCount ?? 0) > 0;
    case "price":
      return product.sellingPrice != null && Number(product.sellingPrice) > 0;
    case "supplier":
      return Boolean(product.hasSupplier);
    case "countryOfOrigin":
      return Boolean(product.countryOfOrigin?.trim());
    case "material":
      return Boolean(product.material?.trim());
    default:
      return true;
  }
}

export function projectProviderReadiness(opts: {
  adapter: MarketplaceReadinessAdapter;
  product: CanonicalProductSnapshot;
  connected: boolean;
  evaluated?: boolean;
  providerError?: string | null;
  needsMapping?: boolean;
  needsApproval?: boolean;
  amazon?: {
    productTypeSelection?: AmazonProductTypeSelection | null;
    schema?: AmazonSchemaIdentity | null;
    evaluation?: AmazonPtdEvaluation | null;
    sellerSpecific?: boolean;
    ptdSchema?: unknown;
    listingShipping?: AmazonListingShippingConfig | null;
    packageFacts?: ShippingDimensions | null;
  };
}): ProviderReadiness {
  const { adapter, product, connected } = opts;
  if (isAmazonProvider(adapter.provider)) {
    return evaluateAmazonAuReadiness({
      connected,
      productTypeSelection: opts.amazon?.productTypeSelection,
      schema: opts.amazon?.schema,
      evaluation: opts.amazon?.evaluation,
      providerError: opts.providerError,
      needsApproval: opts.needsApproval,
      sellerSpecific: opts.amazon?.sellerSpecific,
      ptdSchema: opts.amazon?.ptdSchema,
      listingShipping: opts.amazon?.listingShipping,
      packageFacts: opts.amazon?.packageFacts,
    });
  }
  if (opts.providerError) {
    return {
      provider: adapter.provider,
      displayName: adapter.displayName,
      status: "PROVIDER_ERROR",
      missingFields: [],
      summary: opts.providerError,
    };
  }
  if (!connected) {
    return {
      provider: adapter.provider,
      displayName: adapter.displayName,
      status: "CHANNEL_NOT_CONNECTED",
      missingFields: [],
      summary: "Not connected",
    };
  }
  if (opts.evaluated === false) {
    return {
      provider: adapter.provider,
      displayName: adapter.displayName,
      status: "NOT_EVALUATED",
      missingFields: [],
      summary: "Not evaluated",
    };
  }
  if (opts.needsMapping) {
    return {
      provider: adapter.provider,
      displayName: adapter.displayName,
      status: "NEEDS_MAPPING",
      missingFields: [],
      summary: "Needs mapping",
    };
  }
  if (opts.needsApproval) {
    return {
      provider: adapter.provider,
      displayName: adapter.displayName,
      status: "NEEDS_APPROVAL",
      missingFields: [],
      summary: "Needs approval",
    };
  }
  const missing = adapter.requiredFields.filter((req) => !fieldPresent(product, req.field));
  if (missing.length > 0) {
    return {
      provider: adapter.provider,
      displayName: adapter.displayName,
      status: "MISSING_REQUIRED_DATA",
      missingFields: missing,
      summary: `Missing ${missing.length} field${missing.length === 1 ? "" : "s"}`,
    };
  }
  return {
    provider: adapter.provider,
    displayName: adapter.displayName,
    status: "READY",
    missingFields: [],
    summary: "Ready",
  };
}

/** True when the provider adapter lists GTIN/UPC/EAN as required (not a global Tradivo rule). */
export function providerRequiresProductIdentifier(provider: string | null | undefined): boolean {
  const adapter = builtinMarketplaceAdapters.find((a) => a.provider === provider);
  if (!adapter) return false;
  return adapter.requiredFields.some((f) => f.field === "gtin" || f.field === "upc" || f.field === "ean");
}

export function formatProviderReadinessLine(row: ProviderReadiness): string {
  if (row.status === "READY") return `${row.displayName} Ready ✓`;
  if (row.status === "CHANNEL_NOT_CONNECTED") return `${row.displayName} — Not connected`;
  if (row.status === "NOT_EVALUATED") return `${row.displayName} — Not evaluated`;
  if (row.status === "MISSING_REQUIRED_DATA") {
    const names = row.missingFields.map((f) => f.label).join(", ");
    return `${row.displayName} — ${row.summary}${names ? `: ${names}` : ""}`;
  }
  return `${row.displayName} — ${row.summary}`;
}

export function snapshotFromWizard(form: {
  name: string;
  sku: string;
  brand: string;
  manufacturer: string;
  mpn: string;
  condition: string;
  categoryId: string;
  gtin: string;
  upc: string;
  ean: string;
  barcode: string;
  identifiersNotApplicable: boolean;
  weightKg: string;
  lengthCm: string;
  widthCm: string;
  heightCm: string;
  description: string;
  shortDescription: string;
  sellingPrice: string;
  supplierId?: string;
  countryOfOrigin?: string;
  material?: string;
}, imageCount: number): CanonicalProductSnapshot {
  const n = (v: string) => (v.trim() === "" ? null : Number(v));
  return {
    name: form.name,
    sku: form.sku,
    brand: form.brand,
    manufacturer: form.manufacturer,
    mpn: form.mpn,
    condition: form.condition,
    categoryId: form.categoryId || null,
    gtin: form.gtin,
    upc: form.upc,
    ean: form.ean,
    barcode: form.barcode,
    identifiersNotApplicable: form.identifiersNotApplicable,
    weightKg: n(form.weightKg),
    lengthCm: n(form.lengthCm),
    widthCm: n(form.widthCm),
    heightCm: n(form.heightCm),
    description: form.description,
    shortDescription: form.shortDescription,
    imageCount,
    sellingPrice: n(form.sellingPrice),
    hasSupplier: Boolean(form.supplierId),
    countryOfOrigin: form.countryOfOrigin ?? null,
    material: form.material ?? null,
  };
}
